import { Goal, IGoal } from '@app/models/Goal'
import { ApplicationState } from '@app/store'
import { errorToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest } from '@app/utils/request'
import { List, Record } from 'immutable'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------
export interface IGoalsListState {
  readonly dueDate: string
  readonly error?: string
  readonly filterText: string
  readonly goalType: string
  readonly isLoading: boolean
  readonly items: List<IGoal>
  readonly sortOrder: string
  readonly status: string
  readonly totalItems: number
}

const GoalsListStateRecord = Record({
  dueDate: 'All',
  error: undefined,
  filterText: '',
  goalType: 'All',
  isLoading: false,
  items: List<Goal>(),
  sortOrder: 'LastName',
  status: 'Incomplete',
  totalItems: 0,
})

export class GoalsListState extends GoalsListStateRecord
  implements IGoalsListState {
  public static fromJS(initialState?: Partial<IGoalsListState>) {
    if (!initialState) {
      return new GoalsListState()
    }

    const { items, ...rest } = initialState
    return new GoalsListState({
      items: items ? List(items.map((i) => Goal.fromJS(i))) : undefined,
      ...rest,
    })
  }

  public readonly assignedTo: string
  public readonly dueDate: string
  public readonly error?: string
  public readonly filterText: string
  public readonly goalType: string
  public readonly isLoading: boolean
  public readonly items: List<Goal>
  public readonly sortOrder: string
  public readonly status: string
  public readonly totalItems: number

  constructor(props?: Partial<GoalsListState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum GoalsListActionTypes {
  LoadGoals = 'GOALS_LIST::LOAD_GOALS',
  LoadGoalsFailure = 'GOALS_LIST::LOAD_GOALS_FAILURE',
  LoadGoalsRequest = 'GOALS_LIST::LOAD_GOALS_REQUEST',
  LoadGoalsSuccess = 'GOALS_LIST::LOAD_GOALS_SUCCESS',
  SetDueDate = 'GOALS_LIST::SET_DUE_DATE',
  SetFilterText = 'GOALS_LIST::SET_FILTER_TEXT',
  SetGoalType = 'GOAL_LIST::SET_GOAL_TYPE',
  SetStatus = 'GOALS_LIST::SET_STATUS',
  SetSortOrder = 'GOALS_LIST::SET_SORT_ORDER',
}

interface ILoadGoalsAction {
  append: boolean
  loadComplete?: () => void
  startIndex: number
  stopIndex: number
  type: GoalsListActionTypes.LoadGoals
}

interface ILoadGoalsFailureAction {
  error: string
  type: GoalsListActionTypes.LoadGoalsFailure
}

interface ILoadGoalsRequestAction {
  type: GoalsListActionTypes.LoadGoalsRequest
}

interface ILoadGoalsSuccessAction {
  append: boolean
  items: Goal[]
  totalItems: number
  type: GoalsListActionTypes.LoadGoalsSuccess
}

interface ISetDueDateAction {
  dueDate: string
  type: GoalsListActionTypes.SetDueDate
}

interface ISetFilterTextAction {
  filter: string
  type: GoalsListActionTypes.SetFilterText
}

interface ISetGoalTypeAction {
  goalType: string
  type: GoalsListActionTypes.SetGoalType
}

interface ISetSortOrderAction {
  sortOrder: string
  type: GoalsListActionTypes.SetSortOrder
}

interface ISetStatusAction {
  status: string
  type: GoalsListActionTypes.SetStatus
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ILoadGoalsFailureAction
  | ILoadGoalsRequestAction
  | ILoadGoalsSuccessAction
  | ISetDueDateAction
  | ISetFilterTextAction
  | ISetGoalTypeAction
  | ISetSortOrderAction
  | ISetStatusAction

// -------------------------
// ACTION CREATORS
// -------------------------
function loadGoals(
  startIndex: number,
  stopIndex: number,
  append: boolean,
  loadComplete?: () => void,
): ILoadGoalsAction {
  return {
    append,
    loadComplete,
    startIndex,
    stopIndex,
    type: GoalsListActionTypes.LoadGoals,
  }
}

function loadGoalsFailure(error: string): ILoadGoalsFailureAction {
  return {
    error,
    type: GoalsListActionTypes.LoadGoalsFailure,
  }
}

function loadGoalsRequest(): ILoadGoalsRequestAction {
  return {
    type: GoalsListActionTypes.LoadGoalsRequest,
  }
}

function loadGoalsSuccess(
  items: Goal[],
  append: boolean,
  totalItems: number,
): ILoadGoalsSuccessAction {
  return {
    append,
    items,
    totalItems,
    type: GoalsListActionTypes.LoadGoalsSuccess,
  }
}

function setDueDate(dueDate: string): ISetDueDateAction {
  return {
    dueDate,
    type: GoalsListActionTypes.SetDueDate,
  }
}

function setFilterText(filter: string): ISetFilterTextAction {
  return {
    filter,
    type: GoalsListActionTypes.SetFilterText,
  }
}

function setGoalType(goalType: string): ISetGoalTypeAction {
  return {
    goalType,
    type: GoalsListActionTypes.SetGoalType,
  }
}

function setSortOrder(sortOrder: string) {
  return {
    sortOrder,
    type: GoalsListActionTypes.SetSortOrder,
  }
}

function setStatus(status: string): ISetStatusAction {
  return {
    status,
    type: GoalsListActionTypes.SetStatus,
  }
}

export const actionCreators = {
  loadGoals,
  setDueDate,
  setFilterText,
  setGoalType,
  setSortOrder,
  setStatus,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<GoalsListState> = (
  state: GoalsListState = new GoalsListState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case GoalsListActionTypes.LoadGoalsFailure:
      return state
        .set('error', action.error)
        .set('isLoading', false) as GoalsListState

    case GoalsListActionTypes.LoadGoalsRequest:
      return state.set('isLoading', true) as GoalsListState

    case GoalsListActionTypes.LoadGoalsSuccess:
      const newItems = action.append
        ? (state.items.concat(List(action.items)) as List<Goal>)
        : List(action.items)
      return state
        .set('error', undefined)
        .set('isLoading', false)
        .set('items', newItems)
        .set('totalItems', action.totalItems) as GoalsListState

    case GoalsListActionTypes.SetDueDate:
      return state.set('dueDate', action.dueDate) as GoalsListState

    case GoalsListActionTypes.SetFilterText:
      return state.set('filterText', action.filter) as GoalsListState

    case GoalsListActionTypes.SetGoalType:
      return state.set('goalType', action.goalType) as GoalsListState

    case GoalsListActionTypes.SetSortOrder:
      return state.set('sortOrder', action.sortOrder) as GoalsListState

    case GoalsListActionTypes.SetStatus:
      return state.set('status', action.status) as GoalsListState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* loadGoalsSaga(action: ILoadGoalsAction) {
  try {
    yield put(loadGoalsRequest())

    const currentState: GoalsListState = yield select(
      (state: ApplicationState) => state.goalList,
    )

    const query: string[] = []

    if (currentState.dueDate) {
      query.push(`date=${currentState.dueDate}`)
    }

    if (currentState.filterText) {
      query.push(`filter=${encodeURIComponent(currentState.filterText)}`)
    }

    if (currentState.goalType) {
      query.push(`type=${currentState.goalType}`)
    }

    if (currentState.sortOrder !== 'LastName') {
      query.push(`sort=${currentState.sortOrder}`)
    }

    if (currentState.status) {
      query.push(`status=${currentState.status}`)
    }

    if (action.startIndex > 0) {
      query.push(`skip=${action.startIndex}`)
    }

    const count = action.stopIndex - action.startIndex + 1
    if (count > 0) {
      query.push(`count=${count}`)
    }

    const baseUrl = '/api/Goals/My'

    const url = `${baseUrl}${query.length > 0 ? `?${query.join('&')}` : ''}`

    const result = yield makeAuthenticatedRequest(url)

    if (result.success === true) {
      const contacts: Goal[] = result.results.map((c: any) => Goal.fromJS(c))
      yield put(loadGoalsSuccess(contacts, action.append, result.paging.total))
    } else {
      yield put(loadGoalsFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadGoalsFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchLoadGoals() {
  yield takeLatest(GoalsListActionTypes.LoadGoals, loadGoalsSaga)
}

export function* goalListRootSaga() {
  yield fork(watchLoadGoals)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (state: ApplicationState): GoalsListState =>
  state.goalList

export const selectDueDate = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.dueDate,
  )

export const selectError = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.error,
  )

export const selectFilterText = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.filterText,
  )

export const selectGoalType = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.goalType,
  )

export const selectIsLoading = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.isLoading,
  )

export const selectItems = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.items,
  )

export const selectSortOrder = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.sortOrder,
  )

export const selectStatus = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.status,
  )

export const selectTotalItems = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.totalItems,
  )
